import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Download, Upload } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

function TransactionHistory() {
  const [receipts, setReceipts] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTransactions();
  }, []);

  const fetchTransactions = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [receiptsRes, issuesRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/stock-receipts`, { headers }),
        axios.get(`${BACKEND_URL}/api/stock-issues`, { headers })
      ]);

      setReceipts(receiptsRes.data);
      setIssues(issuesRes.data);
    } catch (error) {
      toast.error('Failed to fetch transaction history');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-slate-600">Loading transaction history...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-4xl font-extrabold text-slate-900 tracking-tight font-heading" data-testid="history-title">Transaction History</h1>
        <p className="text-slate-600 mt-2">View all stock receipts and issues</p>
      </div>

      <Tabs defaultValue="receipts" className="w-full">
        <TabsList className="grid w-full max-w-md grid-cols-2">
          <TabsTrigger value="receipts" data-testid="receipts-tab">
            <Download className="w-4 h-4 mr-2" />
            Receipts ({receipts.length})
          </TabsTrigger>
          <TabsTrigger value="issues" data-testid="issues-tab">
            <Upload className="w-4 h-4 mr-2" />
            Issues ({issues.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="receipts">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full" data-testid="receipts-history-table">
                  <thead className="bg-slate-50">
                    <tr className="border-b border-slate-200">
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Item</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Supplier</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Invoice</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Quantity</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Price</th>
                    </tr>
                  </thead>
                  <tbody>
                    {receipts.map((receipt) => (
                      <tr key={receipt.id} className="hover:bg-slate-50/50 border-b border-slate-100 last:border-0" data-testid={`receipt-history-${receipt.id}`}>
                        <td className="px-6 py-4 text-sm text-slate-600">{new Date(receipt.date).toLocaleDateString()}</td>
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{receipt.item_name}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{receipt.supplier}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{receipt.invoice_number}</td>
                        <td className="px-6 py-4 text-sm">
                          <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200">
                            +{receipt.qty} {receipt.unit}
                          </Badge>
                        </td>
                        <td className="px-6 py-4 text-sm font-semibold text-slate-900">₹{receipt.price}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {receipts.length === 0 && (
                  <div className="p-8 text-center text-slate-600">
                    No receipts found
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="issues">
          <Card className="bg-white border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <div className="rounded-xl border border-slate-200 overflow-hidden">
                <table className="w-full" data-testid="issues-history-table">
                  <thead className="bg-slate-50">
                    <tr className="border-b border-slate-200">
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Date</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Item</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Employee</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Project</th>
                      <th className="px-6 py-3 text-left text-xs font-semibold text-slate-700 uppercase">Quantity</th>
                    </tr>
                  </thead>
                  <tbody>
                    {issues.map((issue) => (
                      <tr key={issue.id} className="hover:bg-slate-50/50 border-b border-slate-100 last:border-0" data-testid={`issue-history-${issue.id}`}>
                        <td className="px-6 py-4 text-sm text-slate-600">{new Date(issue.date).toLocaleDateString()}</td>
                        <td className="px-6 py-4 text-sm font-medium text-slate-900">{issue.item_name}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{issue.employee_name}</td>
                        <td className="px-6 py-4 text-sm text-slate-600">{issue.project}</td>
                        <td className="px-6 py-4 text-sm">
                          <Badge className="bg-red-50 text-red-700 border-red-200">
                            -{issue.quantity}
                          </Badge>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {issues.length === 0 && (
                  <div className="p-8 text-center text-slate-600">
                    No issues found
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}

export default TransactionHistory;